import { Trans } from "react-i18next";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { errorLoggingService } from "../services/errorLoggingService";
import "./FeatureErrorBoundary.css";
import logger from "@shared/utils/logger";

interface Props {
  children: ReactNode;
  featureName?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Keep react-bootstrap out of this boundary so feature routes (e.g. worklist)
 * do not pay vendor-ui just for error UI that rarely mounts.
 */
class FeatureErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      logger.error(`Error in ${this.props.featureName || "feature"}:`, error);
      logger.error("Error info:", errorInfo);
    }

    errorLoggingService.logError(error, {
      feature: this.props.featureName,
      componentStack: errorInfo.componentStack ?? undefined,
      url: window.location.href,
      additionalData: {
        errorBoundary: "FeatureErrorBoundary",
      },
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="card border-warning my-3">
          <div className="card-body text-center py-4">
            <i className="bi bi-exclamation-circle text-warning feature-error-icon"></i>
            <h5 className="mt-3 mb-2">
              {this.props.featureName || "This section"}{" "}
              <Trans ns="features" i18nKey="ui.copy.64fae62cfdb2" />
            </h5>
            <p className="text-muted mb-3">
              <Trans ns="features" i18nKey="ui.copy.e1051e3ff8db" />
            </p>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={this.handleReset}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              <Trans ns="features" i18nKey="ui.copy.cef2fe093b68" />
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FeatureErrorBoundary;
